import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, CircularProgress,
  Alert, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Chip, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getAdminStats, listAdminUsers, listAdminDeals, listAdminInvestments,
  getAuditLog, getComplianceOverview, getOnboardingPipeline,
  advanceOnboarding, rejectOnboarding, updateAdminUser,
} from '../services/api';

const formatCurrency = (v) => v != null
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
  : '—';

const AdminDashboard = () => {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, usersRes, dealsRes] = await Promise.all([
          getAdminStats(),
          listAdminUsers(),
          listAdminDeals(),
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data?.users || usersRes.data || []);
        setDeals(dealsRes.data?.deals || dealsRes.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Lazy-load tab data
  useEffect(() => {
    const loadTab = async () => {
      try {
        if (tab === 2 && investments.length === 0) {
          const res = await listAdminInvestments();
          setInvestments(res.data?.investments || res.data || []);
        }
        if (tab === 3 && auditLog.length === 0) {
          const res = await getAuditLog();
          setAuditLog(res.data?.entries || res.data || []);
        }
        if (tab === 4 && !compliance) {
          const [compRes, pipRes] = await Promise.all([getComplianceOverview(), getOnboardingPipeline()]);
          setCompliance(compRes.data);
          setPipeline(pipRes.data?.pipeline || pipRes.data || []);
        }
      } catch (_) { /* non-critical */ }
    };
    loadTab();
  }, [tab]);

  const handleAdvance = async (id) => {
    try { await advanceOnboarding(id); const res = await getOnboardingPipeline(); setPipeline(res.data?.pipeline || res.data || []); } catch (_) {}
  };
  const handleReject = async (id) => {
    try { await rejectOnboarding(id); const res = await getOnboardingPipeline(); setPipeline(res.data?.pipeline || res.data || []); } catch (_) {}
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  const filteredUsers = users.filter((u) =>
    !searchQuery || (u.name || u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>Admin Dashboard</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>Platform management & oversight</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              { label: 'Total Users', value: stats.totalUsers, icon: <PeopleIcon fontSize="large" /> },
              { label: 'Active Deals', value: stats.activeDeals, icon: <BusinessCenterIcon fontSize="large" /> },
              { label: 'Total Invested', value: formatCurrency(stats.totalInvested), icon: <AssessmentIcon fontSize="large" /> },
              { label: 'Pending KYC', value: stats.pendingKyc, icon: <GavelIcon fontSize="large" /> },
            ].map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: '#478289', mb: 1 }}>{s.icon}</Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#052d50' }}>{s.value ?? '—'}</Typography>
                  <Typography variant="body2" color="textSecondary">{s.label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{ '& .Mui-selected': { color: '#478289' }, '& .MuiTabs-indicator': { backgroundColor: '#478289' } }}>
            <Tab label="Users" />
            <Tab label="Deals" />
            <Tab label="Investments" />
            <Tab label="Audit Log" />
            <Tab label="Compliance & Onboarding" />
          </Tabs>
        </Box>

        {/* Users */}
        {tab === 0 && (
          <>
            <TextField fullWidth size="small" placeholder="Search users..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} sx={{ mb: 2 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
            <TableContainer component={Paper}>
              <Table>
                <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>KYC Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.name || '—'}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell><Chip label={u.role || 'investor'} size="small" /></TableCell>
                      <TableCell>
                        <Chip label={u.kycStatus || 'none'} size="small"
                          color={u.kycStatus === 'approved' ? 'success' : u.kycStatus === 'pending' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Deals */}
        {tab === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Startup</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Raised</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {deals.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{d.startupName || d.startup?.name || '—'}</TableCell>
                    <TableCell>{formatCurrency(d.targetAmount)}</TableCell>
                    <TableCell>{formatCurrency(d.raisedAmount)}</TableCell>
                    <TableCell><Chip label={d.status || 'active'} size="small"
                      color={d.status === 'closed' ? 'success' : d.status === 'cancelled' ? 'error' : 'primary'} /></TableCell>
                    <TableCell>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Investments */}
        {tab === 2 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Investor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Startup</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {investments.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell>{inv.investorName || inv.user?.name || '—'}</TableCell>
                    <TableCell>{inv.startupName || inv.startup?.name || '—'}</TableCell>
                    <TableCell>{formatCurrency(inv.amount)}</TableCell>
                    <TableCell><Chip label={inv.status} size="small" /></TableCell>
                    <TableCell>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Audit Log */}
        {tab === 3 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {auditLog.map((entry, i) => (
                  <TableRow key={entry.id || i} hover>
                    <TableCell><Chip label={entry.action} size="small" /></TableCell>
                    <TableCell>{entry.userName || entry.user?.email || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {typeof entry.details === 'string' ? entry.details : JSON.stringify(entry.details)}
                    </TableCell>
                    <TableCell>{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Compliance & Onboarding */}
        {tab === 4 && (
          <>
            {compliance && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {Object.entries(compliance).map(([key, val]) => (
                  <Grid item xs={6} md={3} key={key}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#478289' }}>{val}</Typography>
                      <Typography variant="body2" color="textSecondary">{key.replace(/([A-Z])/g, ' $1').trim()}</Typography>
                    </CardContent></Card>
                  </Grid>
                ))}
              </Grid>
            )}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Onboarding Pipeline</Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {pipeline.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{p.companyName || p.name || '—'}</TableCell>
                      <TableCell><Chip label={p.stage || p.status} size="small" /></TableCell>
                      <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>
                        <IconButton color="success" size="small" onClick={() => handleAdvance(p.id)}><CheckCircleIcon /></IconButton>
                        <IconButton color="error" size="small" onClick={() => handleReject(p.id)}><CancelIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
    </Box>
  );
};

export default AdminDashboard;
